import React, { useState, useRef } from 'react';
import PageWrapper from '@/components/layout/PageWrapper';
import { META } from '@/config/constants';
import ReCAPTCHA from 'react-google-recaptcha';

const SignUp = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaValue) {
      alert('Please complete the CAPTCHA');
      return;
    }

    // Create the payload with form data and captcha value
    const payload = {
      ...formData,
      recaptchaToken: captchaValue
    };

    // Show loading state
    setIsSubmitting(true);


    // Send data to the API endpoint
    fetch('https://testapis.gocobalt.io/api/v2/cobalt-signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        console.log('Success:', data);
        // Set submitted state instead of showing alert
        setIsSubmitted(true);
        setIsSubmitting(false);

        // Reset form and captcha
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: ''
        });
        setCaptchaValue(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      })
      .catch(error => {
        console.error('Error:', error);
        alert('There was an error submitting your information. Please try again.');
        setIsSubmitting(false);
      });
  };

  const logos = [
    { src: "/clients/incorta.svg", alt: "Incorta" },
    { src: "/clients/ovationcxm.svg", alt: "OvationCXM" },
    { src: "/clients/saleshandy.svg", alt: "Saleshandy" },
    { src: "/clients/velocityglobal.svg", alt: "Velocity Global" },
    { src: "/clients/omni.svg", alt: "Omni" },
    { src: "/clients/rocketium.svg", alt: "Rocketium" },
    { src: "/clients/imagekit.svg", alt: "Imagekit" },
    { src: "/clients/recotap.svg", alt: "Recotap" },
    { src: "/clients/airmeet.svg", alt: "Airmeet" },
    { src: "/clients/recruiterflow.svg", alt: "Recruiterflow" },
  ];

  return (
    <PageWrapper
      title={META.SIGNUP.TITLE}
      description={META.SIGNUP.DESCRIPTION}
      keywords={["sign up", "register", "cobalt account", "embedded iPaaS", "SaaS integration platform"]}
    >
      <div className="flex flex-col md:flex-row min-h-screen">
        {/* Content section - shows second on mobile, first on desktop */}
        <div className="order-2 md:order-1 bg-white p-6 md:p-12 md:w-1/2 flex flex-col justify-center">
          <div className="max-w-md mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Ship <span className="text-blue-500">1,000+</span> native integrations
            </h1>

            <ul className="space-y-3 my-6">
              <li className="flex items-center">
                <div className="bg-blue-500 rounded-full p-1 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span>30 days trial / POC</span>
              </li>
              <li className="flex items-center">
                <div className="bg-blue-500 rounded-full p-1 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span>45 mins free demo</span>
              </li>
              <li className="flex items-center">
                <div className="bg-blue-500 rounded-full p-1 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span>Talk to an integration expert</span>
              </li>
            </ul>

            <h3 className="text-xl font-medium text-gray-700 mt-10 mb-6">You are in good company</h3>

            <div className="grid grid-cols-3 gap-6">
              {logos.slice(0, 9).map((logo, index) => (
                <div key={index} className="flex items-center justify-center">
                  <img src={logo.src} alt={logo.alt} className="h-8 max-w-full" />
                </div>
              ))}
            </div>

            <p className="text-gray-500 mt-6">and many more...</p>
          </div>
        </div>

        {/* Form section - shows first on mobile, second on desktop */}
        <div className="order-1 md:order-2 bg-gray-50 p-6 md:p-12 md:w-1/2 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-bold">Get Started</h2>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">Sign Up for a Trial Account</h1>
              <p className="text-gray-600">
                Enter your details to easily make your new trial account at Cobalt and easily enjoy all the benefits.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jane Smith"
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jane@framer.com"
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 555-1234"
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company"
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="mt-4">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
                    onChange={handleCaptchaChange}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    This site is protected by reCAPTCHA and the Google
                    <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline ml-1">Privacy Policy</a> and
                    <a href="https://policies.google.com/terms" className="text-blue-600 hover:underline ml-1">Terms of Service</a> apply.
                  </p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded-md transition duration-200 mt-4"
                  disabled={!captchaValue || isSubmitting || isSubmitted}
                >
                  {isSubmitted ? 'Thanks! We will get back to you soon!' : isSubmitting ? 'Submitting...' : 'Create an Account'}
                </button>
              </div>
            </form>

            <div className="text-center mt-6">
              <p className="text-gray-600">
                Already have an account yet?
                <a href="https://app.gocobalt.io/login" className="text-blue-600 hover:underline ml-1">Sign In</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default SignUp;